import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { VerisureQrCode, downloadVerisureStickerPng } from '../../components/verisure/VerisureQrCode';
import { useAuth } from '../../context/AuthContext';
import { verisureService } from '../../services/api';
import { VerisureBlankQr } from '../../types';
import { toast } from 'react-hot-toast';
import { BulkQrPrintStationModal } from '../../components/verisure/BulkQrPrintStationModal';
import { VerisureQrCard } from '../../components/verisure/VerisureQrCard';
import { isProductionMode, useEnvironmentMode } from '../../services/productionModeService';
import { subscriptionService, SubscriptionUsage } from '../../services/subscriptionService';
import { UpgradeModal } from '../../components/subscription/UpgradeModal';

const PRESET_OUTLETS = [
  { name: 'Ikeja Computer Village Flagship Hub', location: '14 Otigba St, Ikeja, Lagos' },
  { name: 'Victoria Island Experience Center', location: '280 Akin Adesola St, VI, Lagos' },
  { name: 'Wuse II Tech Center - Abuja', location: 'Plot 412 Aminu Kano Cres, Wuse II, Abuja' },
  { name: 'GRA Phase II Service Point - Port Harcourt', location: '18 Tombia St, GRA Phase 2, Port Harcourt' },
  { name: 'Central Electronics Market - Kano', location: 'Kano Mall Plaza, Kano' },
  { name: 'Custom Outlet', location: 'Enter custom outlet address' },
];

export const BlankQrManagerPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Role Gate: Super Admin & Master Admin only
  const isAuthorized = ['super_admin', 'master_admin'].includes(user?.role || '');

  const [activeTab, setActiveTab] = useState<'inventory' | 'generate' | 'print'>('generate');
  const [blankQrs, setBlankQrs] = useState<VerisureBlankQr[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Batch Generation Form State
  const [selectedOutletPreset, setSelectedOutletPreset] = useState(PRESET_OUTLETS[0].name);
  const [customOutletName, setCustomOutletName] = useState('');
  const [customOutletLocation, setCustomOutletLocation] = useState('');
  const [batchCount, setBatchCount] = useState<number>(10);
  const [batchPrefix, setBatchPrefix] = useState('VRS-TAG-');
  const [batchId, setBatchId] = useState(`BATCH-${new Date().getFullYear()}-${Math.floor(100 + Math.random() * 900)}`);
  const [batchNotes, setBatchNotes] = useState('');

  // Filtering & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'unclaimed' | 'registered'>('all');
  const [outletFilter, setOutletFilter] = useState<string>('all');

  // Preview Modal
  const [selectedPreviewQr, setSelectedPreviewQr] = useState<VerisureBlankQr | null>(null);
  const [previewMode, setPreviewMode] = useState<'sticker' | 'card'>('sticker');

  // Printable Sheet Config
  const [printLayout, setPrintLayout] = useState<'8_per_page' | '12_per_page' | 'single'>('8_per_page');
  const [printFilterBatch, setPrintFilterBatch] = useState<string>('all');
  const [printMaterial, setPrintMaterial] = useState<'transparent' | 'white'>('transparent');
  const [printInkColor, setPrintInkColor] = useState<'#000000' | '#ffffff' | '#ff5a00'>('#000000');
  const [isBulkStationOpen, setIsBulkStationOpen] = useState(false);
  const [subscriptionUsage, setSubscriptionUsage] = useState<SubscriptionUsage | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const { isProduction } = useEnvironmentMode();

  const loadUsage = async () => {
    try {
      const u = await subscriptionService.getUsageAndLimits(user);
      setSubscriptionUsage(u);
    } catch (e) {
      console.warn('Error loading subscription usage in BlankQrManager:', e);
    }
  };

  useEffect(() => {
    loadBlankQrs();
    loadUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProduction, user]);

  const loadBlankQrs = async () => {
    setLoading(true);
    try {
      const res = await verisureService.getBlankQrs();
      let data: VerisureBlankQr[] = res.data || [];
      if ((!data || data.length === 0) && !isProductionMode()) {
        // Initial demo blank tags if completely fresh (Demo Sandbox Mode only)
        const origin = window.location.origin;
        data = [
          {
            id: 'vrs_demo_tag_1',
            tagId: 'VRS-TAG-10492',
            batchId: 'BATCH-2026-IKEJA-01',
            outletName: 'Ikeja Computer Village Flagship Hub',
            outletLocation: '14 Otigba St, Ikeja, Lagos',
            status: 'unclaimed',
            qrPayloadUrl: `${origin}/tag/VRS-TAG-10492`,
            createdBy: user?.id || 'admin',
            createdByName: user?.name || 'Master Admin',
            createdAt: new Date(),
          },
          {
            id: 'vrs_demo_tag_2',
            tagId: 'VRS-TAG-10493',
            batchId: 'BATCH-2026-IKEJA-01',
            outletName: 'Ikeja Computer Village Flagship Hub',
            outletLocation: '14 Otigba St, Ikeja, Lagos',
            status: 'unclaimed',
            qrPayloadUrl: `${origin}/tag/VRS-TAG-10493`,
            createdBy: user?.id || 'admin',
            createdByName: user?.name || 'Master Admin',
            createdAt: new Date(),
          },
          {
            id: 'vrs_demo_tag_3',
            tagId: 'VRS-TAG-20811',
            batchId: 'BATCH-2026-VI-02',
            outletName: 'Victoria Island Experience Center',
            outletLocation: '280 Akin Adesola St, VI, Lagos',
            status: 'registered',
            qrPayloadUrl: `${origin}/tag/VRS-TAG-20811`,
            createdBy: user?.id || 'admin',
            createdByName: user?.name || 'Master Admin',
            createdAt: new Date(Date.now() - 86400000 * 2),
            claimedAt: new Date(Date.now() - 86400000),
            claimedByName: 'Amina Yusuf',
            claimedByEmail: 'amina.yusuf@example.com',
            deviceSummary: {
              make: 'Apple',
              model: 'iPhone 15 Pro Max (256GB Titanium)',
              category: 'consumer_electronics',
              serialNumber: 'F2LLM89KQ6',
            },
          },
        ];
      }
      setBlankQrs(data);
    } catch (err: any) {
      toast.error('Failed to load blank QR codes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenerating(true);

    const isCustom = selectedOutletPreset === 'Custom Outlet';
    const finalOutletName = isCustom ? customOutletName : selectedOutletPreset;
    const matchedPreset = PRESET_OUTLETS.find((p) => p.name === selectedOutletPreset);
    const finalOutletLocation = isCustom ? customOutletLocation : matchedPreset?.location || '';

    if (!finalOutletName) {
      toast.error('Please provide an outlet name');
      setGenerating(false);
      return;
    }

    // Check organization freemium limit (10 QR codes on Free plan)
    const check = await subscriptionService.canRegisterQr(user, Number(batchCount));
    if (!check.allowed) {
      setSubscriptionUsage(check.usage);
      setIsUpgradeModalOpen(true);
      toast.error(check.message || 'Freemium QR limit reached. Please upgrade to Premium.');
      setGenerating(false);
      return;
    }

    try {
      const res = await verisureService.createBlankQrBatch({
        outletName: finalOutletName,
        outletLocation: finalOutletLocation,
        batchId: batchId.trim(),
        count: Number(batchCount),
        prefix: batchPrefix.trim(),
        notes: batchNotes,
      });

      const newBatch: VerisureBlankQr[] = res.data;
      setBlankQrs([...newBatch, ...blankQrs]);
      toast.success(`Successfully generated ${newBatch.length} blank QR codes for ${finalOutletName}!`);
      
      // Reset Batch ID with new random suffix
      setBatchId(`BATCH-${new Date().getFullYear()}-${Math.floor(100 + Math.random() * 900)}`);
      setBatchNotes('');
      // Switch to printable sheet view
      setActiveTab('print');
    } catch (err: any) {
      toast.error('Generation failed: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBulkDeleteQrs = async (idsOrTagIds: string[]) => {
    try {
      await verisureService.deleteBlankQrsBulk(idsOrTagIds);
      const toDeleteSet = new Set(idsOrTagIds);
      setBlankQrs((prev) =>
        prev.filter((q) => !toDeleteSet.has(q.id || '') && !toDeleteSet.has(q.tagId))
      );
      toast.success(`Successfully deleted ${idsOrTagIds.length} QR code(s).`);
    } catch (err: any) {
      toast.error('Failed to delete QR codes: ' + err.message);
      throw err;
    }
  };

  const handleDeleteSingleQr = async (qr: VerisureBlankQr) => {
    const isUsed = qr.status === 'registered';
    const confirmMsg = isUsed
      ? `Permanently delete registered tag "${qr.tagId}"? This QR has already been bound to an appliance and customer.`
      : `Permanently delete blank unassigned tag "${qr.tagId}"?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      await verisureService.deleteBlankQr(qr.id || qr.tagId);
      setBlankQrs((prev) => prev.filter((q) => q.tagId !== qr.tagId && q.id !== qr.id));
      toast.success(`Tag ${qr.tagId} deleted successfully.`);
    } catch (err: any) {
      toast.error('Failed to delete tag: ' + err.message);
    }
  };

  const handlePurgeUsedTagsInLedger = async () => {
    const usedTags = blankQrs.filter((q) => q.status === 'registered');
    if (usedTags.length === 0) {
      toast('No registered/used tags found in inventory.', { icon: 'ℹ️' });
      return;
    }
    if (
      !window.confirm(
        `Are you sure you want to permanently delete all ${usedTags.length} registered/used QR code(s) from inventory? This action cannot be undone.`
      )
    ) {
      return;
    }
    try {
      const idsToDelete = usedTags.map((q) => q.id || q.tagId);
      await handleBulkDeleteQrs(idsToDelete);
    } catch (err: any) {
      // Handled in handleBulkDeleteQrs
    }
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center text-3xl mb-4 border border-red-500/20">
          🔒
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Restricted Access</h1>
        <p className="text-slate-400 max-w-md mb-6 text-sm">
          Generating blank physical QR codes for retail outlet pickup requires <strong>Master Admin</strong> or <strong>Super Admin</strong> authorization.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-6 py-2.5 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-700 transition"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  // Filtered list for Inventory
  const filteredQrs = blankQrs.filter((item) => {
    const matchesSearch =
      item.tagId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.outletName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.batchId && item.batchId.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.deviceSummary?.make && item.deviceSummary.make.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.deviceSummary?.model && item.deviceSummary.model.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.claimedByName && item.claimedByName.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus =
      statusFilter === 'all'
        ? true
        : statusFilter === 'unclaimed'
        ? item.status === 'unclaimed'
        : item.status === 'registered';

    const matchesOutlet =
      outletFilter === 'all' ? true : item.outletName === outletFilter;

    return matchesSearch && matchesStatus && matchesOutlet;
  });

  // Extract distinct outlets and batches for dropdowns
  const distinctOutlets = Array.from(new Set(blankQrs.map((q) => q.outletName))).filter(Boolean);
  const distinctBatches = Array.from(new Set(blankQrs.map((q) => q.batchId))).filter(Boolean);

  // Items for print sheet
  const printItems = blankQrs.filter((item) => {
    if (printFilterBatch !== 'all' && item.batchId !== printFilterBatch) return false;
    return true;
  });

  const printablePrintInkColor = printInkColor === '#ffffff' ? '#000000' : printInkColor;

  return (
    <div className="p-3 sm:p-6 md:p-8 max-w-7xl mx-auto font-sans min-w-0 max-w-full overflow-hidden">
      {/* Print Specific CSS */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 8mm 6mm;
          }
          html, body {
            background: #ffffff !important;
            color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            overflow: visible !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          header, nav, footer, .no-print, .print-hide {
            display: none !important;
          }
          .printable-sheet-container {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            box-shadow: none !important;
            border: none !important;
            width: 100% !important;
            color: #000000 !important;
          }
          /* Force visibility of sheet and all QR badges */
          .printable-sheet-container,
          .printable-sheet-container *,
          .sticker-badge,
          .sticker-badge * {
            visibility: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .sticker-badge {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            border: 1px dashed #94a3b8 !important;
            background: #ffffff !important;
            color: ${printablePrintInkColor} !important;
            box-shadow: none !important;
          }
          .sticker-badge svg {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .sticker-badge img {
            max-height: 26px !important;
            width: auto !important;
            display: block !important;
            margin: 0 auto !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      {/* Header */}
      <div className="no-print mb-6 sm:mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 min-w-0 max-w-full">
        <div className="min-w-0 max-w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1 glass-pill-container text-orange-400 text-[11px] font-bold rounded-full mb-3 shadow-sm border border-orange-500/25">
            <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></span>
            <span className="tracking-wider uppercase">OUTLET SUPPLY CHAIN STUDIO</span>
          </div>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight flex items-center gap-3">
            Blank Outlet QR Studio
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-1.5 sm:mt-2 max-w-2xl font-normal leading-relaxed">
            Mint and distribute official unassigned physical QR stickers for partner retail outlets. Once affixed, users bind hardware specs in seconds, enabling an unbroken chain of custody for technician repairs.
          </p>
        </div>

        {/* Financial Metric Stat Cards */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 w-full md:w-auto min-w-0">
          <div className="glass-card p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-white/[0.08] min-w-0 text-left">
            <div className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">Total Minted</div>
            <div className="text-xl sm:text-2xl font-black text-white mt-0.5 sm:mt-1 tracking-tight">{blankQrs.length}</div>
            <div className="text-[9px] sm:text-[10px] text-slate-400 font-medium mt-0.5 truncate">Stickers</div>
          </div>
          <div className="glass-card p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-white/[0.08] min-w-0 text-left">
            <div className="text-[9px] sm:text-[10px] font-bold text-amber-400 uppercase tracking-wider flex items-center gap-1 truncate">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0"></span> Stock
            </div>
            <div className="text-xl sm:text-2xl font-black text-amber-400 mt-0.5 sm:mt-1 tracking-tight">
              {blankQrs.filter((q) => q.status === 'unclaimed').length}
            </div>
            <div className="text-[9px] sm:text-[10px] text-amber-400/80 font-medium mt-0.5 truncate">In Outlets</div>
          </div>
          <div className="glass-card p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-white/[0.08] min-w-0 text-left">
            <div className="text-[9px] sm:text-[10px] font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-1 truncate">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0"></span> Claimed
            </div>
            <div className="text-xl sm:text-2xl font-black text-emerald-400 mt-0.5 sm:mt-1 tracking-tight">
              {blankQrs.filter((q) => q.status === 'registered').length}
            </div>
            <div className="text-[9px] sm:text-[10px] text-emerald-400/80 font-medium mt-0.5 truncate">Passports</div>
          </div>
        </div>
      </div>

      {/* Floating Capsule Segmented Switcher */}
      <div className="no-print glass-pill-container p-1 sm:p-1.5 rounded-2xl sm:rounded-full flex gap-1 sm:gap-1.5 mb-6 sm:mb-8 overflow-x-auto max-w-full scrollbar-none touch-pan-x">
        <button
          onClick={() => setActiveTab('generate')}
          className={`px-3.5 sm:px-5 py-2 rounded-xl sm:rounded-full font-bold text-xs transition-all flex items-center gap-2 whitespace-nowrap shrink-0 ${
            activeTab === 'generate'
              ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/25'
              : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          Batch Minting Studio
        </button>
        <button
          onClick={() => setActiveTab('print')}
          className={`px-3.5 sm:px-5 py-2 rounded-xl sm:rounded-full font-bold text-xs transition-all flex items-center gap-2 whitespace-nowrap shrink-0 ${
            activeTab === 'print'
              ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/25'
              : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          Printable Label Sheets ({printItems.length})
        </button>
        <button
          onClick={() => setActiveTab('inventory')}
          className={`px-3.5 sm:px-5 py-2 rounded-xl sm:rounded-full font-bold text-xs transition-all flex items-center gap-2 whitespace-nowrap shrink-0 ${
            activeTab === 'inventory'
              ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/25'
              : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          Inventory & Tag Ledger ({blankQrs.length})
        </button>
      </div>

      {/* TAB 1: BATCH GENERATOR */}
      {activeTab === 'generate' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <form onSubmit={handleGenerateBatch} className="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-6 shadow-xl">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                Configure Outlet Batch
              </h2>
              <p className="text-xs text-slate-400 mt-1">
                Configure physical outlet allocation and sticker count. Each sticker receives a high-security unique serial number and activation URL.
              </p>
            </div>

            {/* Target Outlet Selection */}
            <div className="space-y-3">
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider">
                1. Target Retail Outlet / Service Center
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PRESET_OUTLETS.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => setSelectedOutletPreset(preset.name)}
                    className={`text-left p-3 rounded-2xl border transition text-xs ${
                      selectedOutletPreset === preset.name
                        ? 'bg-orange-500/15 border-orange-500 text-white font-bold shadow-md shadow-orange-500/10'
                        : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                    }`}
                  >
                    <div className="font-bold truncate">{preset.name}</div>
                    <div className="text-[10px] text-slate-400 truncate mt-0.5">{preset.location}</div>
                  </button>
                ))}
              </div>

              {selectedOutletPreset === 'Custom Outlet' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1">Custom Outlet Name</label>
                    <input
                      type="text"
                      value={customOutletName}
                      onChange={(e) => setCustomOutletName(e.target.value)}
                      placeholder="e.g. Surulere Retail Hub"
                      className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1">Address / Region</label>
                    <input
                      type="text"
                      value={customOutletLocation}
                      onChange={(e) => setCustomOutletLocation(e.target.value)}
                      placeholder="e.g. Adeniran Ogunsanya, Lagos"
                      className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider">
                  2. Quantity of Blank Stickers to Generate
                </label>
                <span className="text-xs font-mono text-orange-400 font-bold">{batchCount} Stickers</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[5, 10, 20, 50, 100].map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setBatchCount(num)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition border ${
                      batchCount === num
                        ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white border-orange-400'
                        : 'bg-slate-950 border-slate-800 text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    {num} Stickers
                  </button>
                ))}
              </div>
            </div>

            {/* Prefix & Batch ID */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                  ID Prefix
                </label>
                <input
                  type="text"
                  value={batchPrefix}
                  onChange={(e) => setBatchPrefix(e.target.value)}
                  placeholder="VRS-TAG-"
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-orange-500"
                  required
                />
                <span className="text-[10px] text-slate-500 mt-1 block">Example: {batchPrefix}48912</span>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                  Batch Code
                </label>
                <input
                  type="text"
                  value={batchId}
                  onChange={(e) => setBatchId(e.target.value)}
                  placeholder="BATCH-2026-01"
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-orange-500"
                  required
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Supply Chain Dispatch Notes (Optional)
              </label>
              <textarea
                value={batchNotes}
                onChange={(e) => setBatchNotes(e.target.value)}
                placeholder="e.g. Dispatched to Ikeja retail stock room for customer onboarding."
                rows={2}
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl p-3 text-sm focus:outline-none focus:border-orange-500 resize-none"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={generating}
              className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-black py-4 rounded-2xl shadow-xl shadow-orange-500/20 transition flex items-center justify-center gap-2 text-base disabled:opacity-50"
            >
              {generating ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  <span>Generating & Registering {batchCount} Blank QR Stickers...</span>
                </>
              ) : (
                <span>Generate & Issue {batchCount} Blank QR Stickers</span>
              )}
            </button>
          </form>

          {/* Live Prototype Preview of Single Sticker */}
          <div className="lg:col-span-5 space-y-4">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Physical Sticker Visual Prototype
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 flex flex-col items-center">
              {/* Sticker Card Component */}
              <div className="w-[300px] bg-white rounded-2xl p-4 text-slate-950 shadow-2xl border-2 border-slate-300 relative overflow-hidden flex flex-col items-center">
                {/* Top Corner Ribbon */}
                <div className="absolute -top-6 -right-6 w-20 h-20 bg-orange-500 rotate-45 flex items-end justify-center pb-1">
                  <span className="text-[7px] text-white font-black tracking-widest uppercase">BLANK</span>
                </div>

                {/* Header */}
                <div className="w-full flex items-center justify-between border-b border-slate-200 pb-2 mb-2">
                  <span className="text-[7.5px] bg-red-50 text-red-700 px-2 py-0.5 rounded-full font-extrabold uppercase tracking-wider border border-red-200">
                    OFFICIAL SECURITY TAG
                  </span>
                  <span className="text-[8px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-bold font-mono">OUTLET TAG</span>
                </div>

                {/* Brand Logo & Inscription: "I am Verisure" */}
                <div className="flex flex-col items-center justify-center my-1">
                  <img src="/official.png" alt="Verisure Logo" className="h-9 w-auto object-contain" />
                  <div className="text-center mt-1 mb-2">
                    <div className="text-sm font-black tracking-tight text-slate-900 leading-none">
                      I am <span className="text-[#ff5a00] bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 bg-clip-text text-transparent">Verisure</span>
                    </div>
                    <div className="w-10 h-0.5 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 mx-auto mt-1 rounded-full"></div>
                  </div>
                </div>

                {/* QR Code */}
                <div className="p-2.5 bg-white border border-slate-200 rounded-xl shadow-inner mb-2">
                  <VerisureQrCode
                    value={`${window.location.origin}/tag/${batchPrefix}SAMPLE`}
                    size={140}
                    level="H"
                    includeMargin={false}
                    showCenterBadge={true}
                    badgeTheme="light"
                  />
                </div>

                {/* Tag ID Display */}
                <div className="text-center w-full mb-2">
                  <div className="font-mono text-sm font-black text-slate-900 tracking-wider">
                    {batchPrefix}XXXXX
                  </div>
                  <div className="text-[9px] text-slate-500 font-semibold truncate max-w-[260px] mx-auto">
                    {selectedOutletPreset}
                  </div>
                </div>

                {/* Customer Instructions on Sticker */}
                <div className="w-full bg-orange-50 border border-orange-200 rounded-lg p-2 text-[9px] text-orange-950 space-y-0.5 text-left mb-1 font-medium">
                  <div className="font-bold flex items-center gap-1 text-orange-700">
                    <span>📲</span> How to activate:
                  </div>
                  <div>1. Peel & stick firmly to your device</div>
                  <div>2. Scan QR with phone camera to register</div>
                  <div>3. All future technician repairs link automatically</div>
                </div>

                {/* Tamper Proof Security Bottom */}
                <div className="text-[7px] text-slate-400 font-mono tracking-widest uppercase mt-1">
                  OFFICIAL VERISURE TAMPER-RESISTANT TAG
                </div>
              </div>

              <p className="text-slate-400 text-xs text-center mt-4 max-w-xs">
                Printed tags can be affixed by retail store clerks or picked up by customers at checkout counters.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: PRINTABLE STICKER SHEETS */}
      {activeTab === 'print' && (
        <div className="space-y-6">
          {/* Controls Bar */}
          <div className="no-print bg-slate-900 border border-slate-800 rounded-3xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Batch Filter */}
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Filter by Batch</label>
                <select
                  value={printFilterBatch}
                  onChange={(e) => setPrintFilterBatch(e.target.value)}
                  className="bg-slate-950 border border-slate-700 text-white rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-orange-500"
                >
                  <option value="all">All Batches ({blankQrs.length} tags)</option>
                  {distinctBatches.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>

              {/* Layout */}
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Sheet Layout</label>
                <select
                  value={printLayout}
                  onChange={(e: any) => setPrintLayout(e.target.value)}
                  className="bg-slate-950 border border-slate-700 text-white rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-orange-500"
                >
                  <option value="8_per_page">Standard A4 (8 Stickers Grid)</option>
                  <option value="12_per_page">Compact A4 (12 Stickers Grid)</option>
                  <option value="single">Single Badge Print</option>
                </select>
              </div>

              {/* Material Toggle: Transparent vs White */}
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Sticker Material</label>
                <div className="flex rounded-xl bg-slate-950 border border-slate-800 p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setPrintMaterial('transparent');
                      if (printInkColor === '#ffffff') setPrintInkColor('#000000');
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                      printMaterial === 'transparent'
                        ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <span>Clear Vinyl (Appliances)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPrintMaterial('white')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                      printMaterial === 'white'
                        ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <span>White Paper</span>
                  </button>
                </div>
              </div>

              {/* Ink Color */}
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">QR Ink Color</label>
                <div className="flex gap-1.5 bg-slate-950 border border-slate-800 p-1 rounded-xl">
                  <button
                    type="button"
                    onClick={() => setPrintInkColor('#000000')}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1 ${
                      printInkColor === '#000000' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                    title="Black ink"
                  >
                    <span className="w-2 h-2 rounded-full bg-black border border-white/40"></span>
                    <span>Black</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPrintInkColor('#ff5a00')}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1 ${
                      printInkColor === '#ff5a00' ? 'bg-orange-500/20 text-orange-400' : 'text-slate-400 hover:text-white'
                    }`}
                    title="Verisure Orange"
                  >
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                    <span>Orange</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPrintInkColor('#ffffff')}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1 ${
                      printInkColor === '#ffffff' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                    title="White ink (for black appliances)"
                  >
                    <span className="w-2 h-2 rounded-full bg-white"></span>
                    <span>White</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {blankQrs.some((q) => q.status === 'registered') && (
                <button
                  type="button"
                  onClick={handlePurgeUsedTagsInLedger}
                  className="px-4 py-3 rounded-2xl bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold border border-red-500/30 transition flex items-center gap-1.5"
                  title="Bulk delete registered/used tags from print station inventory"
                >
                  <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Purge Used ({blankQrs.filter((q) => q.status === 'registered').length})</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsBulkStationOpen(true)}
                className="px-4 py-3 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold border border-slate-700 transition flex items-center gap-1.5"
                title="Open Advanced Bulk Print Station with Custom Selection"
              >
                <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
                <span>Bulk Print Station</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  const originalTitle = document.title;
                  document.title = `VERISURE-Blank-Outlet-Tags-${printFilterBatch}`;
                  toast.success('In the print dialog, choose "Save as PDF" to download the PDF.', { duration: 4000 });
                  window.print();
                  setTimeout(() => { document.title = originalTitle; }, 1500);
                }}
                className="px-4 py-3 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold border border-slate-700 transition flex items-center gap-1.5"
                title="Save blank sheet as PDF"
              >
                <svg className="w-4 h-4 text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="12" y1="18" x2="12" y2="12"></line>
                  <line x1="9" y1="15" x2="15" y2="15"></line>
                </svg>
                <span>Save as PDF</span>
              </button>
              <button
                onClick={handlePrint}
                className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-bold px-6 py-3 rounded-2xl shadow-lg shadow-orange-500/20 transition flex items-center gap-2 text-sm"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 6 2 18 2 18 9"/>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                  <rect x="6" y="14" width="12" height="8"/>
                </svg>
                <span>Print {printMaterial === 'transparent' ? 'Transparent' : 'Paper'} Sheet</span>
              </button>
            </div>
          </div>

          {/* Sheet Container for Screen & Print */}
          <div
            className={`printable-sheet-container rounded-3xl p-6 md:p-8 shadow-2xl min-h-[600px] border transition-all ${
              printMaterial === 'transparent'
                ? 'bg-[radial-gradient(#334155_1px,transparent_1px)] [background-size:16px_16px] bg-slate-950/85 border-slate-800 text-white'
                : 'bg-white text-slate-950 border-slate-200'
            }`}
          >
            {/* Sheet Title for Print Header */}
            <div
              className={`border-b pb-3 mb-6 flex justify-between items-center text-xs ${
                printMaterial === 'transparent' ? 'border-slate-800 text-slate-400' : 'border-slate-200 text-slate-500'
              }`}
            >
              <div className="font-bold flex items-center gap-2">
                <img src="/official.png" alt="Verisure" className="h-5 w-auto object-contain" />
                <span>
                  VERISURE CERTIFIED OUTLET STICKER SHEET &bull;{' '}
                  <span className="font-mono text-[10px] uppercase text-orange-400">
                    {printMaterial === 'transparent' ? 'CLEAR VINYL APPLIANCE STICKER' : 'WHITE OPAQUE PAPER'}
                  </span>
                </span>
              </div>
              <div className="font-mono">
                Batch: {printFilterBatch === 'all' ? 'All Batches' : printFilterBatch} • Total: {printItems.length} Stickers
              </div>
            </div>

            {/* Sticker Grid */}
            <div
              className={`grid gap-4 ${
                printLayout === '12_per_page'
                  ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'
                  : printLayout === 'single'
                  ? 'grid-cols-1 max-w-sm mx-auto'
                  : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
              }`}
            >
              {printItems.map((qr) => (
                <div
                  key={qr.id || qr.tagId}
                  className={`sticker-badge rounded-2xl p-3.5 flex flex-col items-center justify-between text-center relative overflow-hidden transition-all ${
                    printMaterial === 'transparent'
                      ? 'border border-dashed border-white/20 bg-white/[0.04] text-slate-100 print:bg-white print:text-black'
                      : 'border-2 border-dashed border-slate-300 bg-white shadow-sm text-slate-900 print:bg-white print:text-black'
                  }`}
                  style={{
                    color: printInkColor === '#ffffff' ? undefined : printInkColor,
                  }}
                >
                  {/* Status Indicator for print */}
                  <div
                    className={`w-full flex items-center justify-between border-b pb-1 mb-1 text-[8px] font-bold ${
                      printMaterial === 'transparent' ? 'border-white/10 print:border-slate-300' : 'border-slate-100'
                    }`}
                  >
                    <span className="text-orange-500 uppercase tracking-wider flex items-center gap-1 font-black text-[7.5px]">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block"></span>
                      VERISURE OFFICIAL
                    </span>
                    <span className="font-mono opacity-60 print:opacity-90">{qr.batchId || 'OUTLET'}</span>
                  </div>

                  {/* Official Verisure Logo */}
                  <div className="flex items-center justify-center my-0.5">
                    <img
                      src="/official.png"
                      alt="Verisure Logo"
                      className={`${printLayout === '12_per_page' ? 'h-5' : 'h-6'} w-auto object-contain`}
                    />
                  </div>

                  {/* Inscription: "I am Verisure" */}
                  <div className="text-center mb-1">
                    <div className={`font-black tracking-tight ${printLayout === '12_per_page' ? 'text-[9.5px]' : 'text-[11px]'} leading-tight print:text-black`}>
                      I am <span className="text-[#ff5a00] print:text-[#ff5a00] font-black">Verisure</span>
                    </div>
                    <div className="w-8 h-0.5 bg-[#ff5a00] mx-auto mt-0.5 rounded-full print:bg-[#ff5a00]"></div>
                  </div>

                  {/* QR Code */}
                  <div
                    className={`p-1.5 rounded-xl my-1 ${
                      printMaterial === 'transparent'
                        ? 'bg-transparent'
                        : 'bg-white border border-slate-200 shadow-inner print:border-none print:shadow-none'
                    }`}
                  >
                    <VerisureQrCode
                      value={`${window.location.origin}/tag/${qr.tagId}`}
                      size={printLayout === '12_per_page' ? 88 : 108}
                      level="H"
                      bgColor="transparent"
                      fgColor={printablePrintInkColor}
                      includeMargin={false}
                      showCenterBadge={true}
                      badgeTheme={printMaterial === 'transparent' && printInkColor !== '#000000' ? 'dark' : 'light'}
                    />
                  </div>

                  {/* Tag ID */}
                  <div className="my-1.5">
                    <div className="font-mono font-black text-xs tracking-wider print:text-black">
                      {qr.tagId}
                    </div>
                    <div className="text-[8px] opacity-70 truncate max-w-[170px] print:opacity-90 print:text-slate-600">
                      {qr.outletName}
                    </div>
                  </div>

                  {/* Instructions */}
                  <div
                    className={`w-full rounded p-1 text-[7.5px] leading-tight ${
                      printMaterial === 'transparent'
                        ? 'border border-white/10 bg-white/[0.03] text-slate-300 print:border-slate-300 print:bg-slate-50 print:text-slate-800'
                        : 'bg-slate-50 border border-slate-200 text-slate-600'
                    }`}
                  >
                    <span className="font-bold text-orange-500">Appliance Tag:</span> Peel & stick to device. Scan with camera to track repairs.
                  </div>
                </div>
              ))}
            </div>

            {printItems.length === 0 && (
              <div className="p-12 text-center text-slate-400">
                No blank QR stickers match the selected filter.
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: TAG INVENTORY LEDGER */}
      {activeTab === 'inventory' && (
        <div className="space-y-6">
          {/* Filter Bar */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-1 items-center gap-3 w-full md:w-auto">
              {/* Search */}
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search Tag ID, Outlet, Device Make, or Owner..."
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl pl-9 pr-4 py-2.5 text-xs focus:outline-none focus:border-orange-500"
                />
                <span className="absolute left-3 top-2.5 text-slate-500">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e: any) => setStatusFilter(e.target.value)}
                className="bg-slate-950 border border-slate-700 text-white rounded-xl px-3 py-2.5 text-xs focus:outline-none focus:border-orange-500"
              >
                <option value="all">All Statuses</option>
                <option value="unclaimed">Unclaimed / Blank Only</option>
                <option value="registered">Registered / Bound Only</option>
              </select>

              {/* Outlet Filter */}
              <select
                value={outletFilter}
                onChange={(e) => setOutletFilter(e.target.value)}
                className="bg-slate-950 border border-slate-700 text-white rounded-xl px-3 py-2.5 text-xs focus:outline-none focus:border-orange-500"
              >
                <option value="all">All Outlets</option>
                {distinctOutlets.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              {blankQrs.some((q) => q.status === 'registered') && (
                <button
                  onClick={handlePurgeUsedTagsInLedger}
                  className="px-4 py-2.5 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-400 font-bold rounded-xl text-xs flex items-center gap-1.5 transition whitespace-nowrap"
                  title="Permanently remove all claimed/used QR codes"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Purge Used Tags ({blankQrs.filter((q) => q.status === 'registered').length})</span>
                </button>
              )}
              <button
                onClick={() => setActiveTab('generate')}
                className="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 transition whitespace-nowrap"
              >
                <span>+</span> Generate More Tags
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
            <div className="sm:hidden px-4 py-2 bg-slate-950/70 text-[10px] text-slate-400 flex items-center justify-between border-b border-slate-800 font-medium">
              <span>Swipe table horizontally for all columns</span>
              <span className="text-orange-400 font-bold">&rarr;</span>
            </div>
            <div className="overflow-x-auto mobile-table-scroll-container">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-950/80 text-slate-400 uppercase tracking-wider font-bold border-b border-slate-800">
                  <tr>
                    <th className="p-4">Tag ID</th>
                    <th className="p-4">QR Preview</th>
                    <th className="p-4">Outlet Provenance</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Linked Device / Owner</th>
                    <th className="p-4">Batch ID</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60 text-slate-300">
                  {filteredQrs.map((qr) => (
                    <tr key={qr.id || qr.tagId} className="hover:bg-slate-800/40 transition">
                      {/* Tag ID */}
                      <td className="p-4">
                        <div className="font-mono font-bold text-white text-sm">{qr.tagId}</div>
                        <div className="text-[10px] text-slate-500">
                          {new Date(qr.createdAt?.seconds ? qr.createdAt.seconds * 1000 : qr.createdAt || Date.now()).toLocaleDateString()}
                        </div>
                      </td>

                      {/* QR Thumbnail */}
                      <td className="p-4">
                        <div
                          onClick={() => setSelectedPreviewQr(qr)}
                          className="w-10 h-10 bg-white p-1 rounded-lg cursor-pointer hover:scale-110 transition border border-slate-700 flex items-center justify-center"
                          title="Click to expand"
                        >
                          <VerisureQrCode value={`${window.location.origin}/tag/${qr.tagId}`} size={32} level="M" showCenterBadge={false} />
                        </div>
                      </td>

                      {/* Outlet */}
                      <td className="p-4">
                        <div className="font-bold text-white">{qr.outletName}</div>
                        <div className="text-[10px] text-slate-400 truncate max-w-xs">{qr.outletLocation || 'Storefront Stock'}</div>
                      </td>

                      {/* Status */}
                      <td className="p-4">
                        {qr.status === 'unclaimed' ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                            Blank / Unclaimed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                            Registered & Bound
                          </span>
                        )}
                      </td>

                      {/* Linked Device */}
                      <td className="p-4">
                        {qr.status === 'registered' ? (
                          <div>
                            <div className="font-bold text-white">
                              {qr.deviceSummary?.make} {qr.deviceSummary?.model}
                            </div>
                            <div className="text-[10px] text-slate-400">
                              Owner: {qr.claimedByName || 'Registered User'}
                              {qr.deviceSummary?.serialNumber && ` • SN: ${qr.deviceSummary.serialNumber}`}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-500 italic text-[11px]">Pending customer pickup</span>
                        )}
                      </td>

                      {/* Batch */}
                      <td className="p-4 font-mono text-[11px] text-slate-400">{qr.batchId || 'N/A'}</td>

                      {/* Actions */}
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedPreviewQr(qr)}
                            className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition"
                            title="Preview & Print Sticker"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                          <button
                            onClick={() => handleCopy(`${window.location.origin}/tag/${qr.tagId}`)}
                            className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition"
                            title="Copy Tag Activation URL"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                            </svg>
                          </button>
                          {qr.status === 'registered' ? (
                            <button
                              onClick={() => navigate(`/item/${qr.tagId}`)}
                              className="px-2.5 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 font-bold rounded-lg text-[11px] transition"
                            >
                              View Repairs &rarr;
                            </button>
                          ) : (
                            <button
                              onClick={() => window.open(`/tag/${qr.tagId}`, '_blank')}
                              className="px-2.5 py-1 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-400 font-bold rounded-lg text-[11px] transition"
                            >
                              Test Tag Flow &rarr;
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteSingleQr(qr)}
                            className="p-1.5 bg-slate-800 hover:bg-red-500/20 text-slate-400 hover:text-red-400 rounded-lg transition"
                            title={`Delete Tag ${qr.tagId}`}
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredQrs.length === 0 && (
                <div className="p-12 text-center text-slate-400">
                  {loading ? 'Loading blank tags...' : 'No blank tags match the search filters.'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Individual Sticker Preview Modal */}
      {selectedPreviewQr && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-md w-full p-6 space-y-6 relative shadow-2xl">
            <button
              onClick={() => setSelectedPreviewQr(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white text-xl"
            >
              ✕
            </button>

            <div className="text-center">
              <h3 className="text-xl font-bold text-white">Official Blank Outlet Sticker</h3>
              <p className="text-xs text-slate-400 mt-1">
                Tag ID: <span className="font-mono text-orange-400 font-bold">{selectedPreviewQr.tagId}</span>
              </p>
            </div>

            {/* View Switcher: Physical Sticker vs Official ID Card */}
            <div className="flex bg-slate-950 p-1 rounded-xl border border-slate-800">
              <button
                type="button"
                onClick={() => setPreviewMode('sticker')}
                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition ${
                  previewMode === 'sticker'
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Physical Sticker View
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode('card')}
                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition ${
                  previewMode === 'card'
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Official ID Card & Export
              </button>
            </div>

            {previewMode === 'sticker' ? (
              /* Sticker Preview Box */
              <div className="bg-white rounded-2xl p-5 text-slate-950 shadow-inner flex flex-col items-center border border-slate-300">
                <div className="w-full flex justify-between items-center border-b border-slate-200 pb-2 mb-2">
                  <span className="text-[7.5px] bg-red-50 text-red-700 px-2 py-0.5 rounded-full font-extrabold uppercase tracking-wider border border-red-200">
                    CERTIFIED SECURITY TAG
                  </span>
                  <span className="text-[8px] bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded font-bold font-mono">
                    {selectedPreviewQr.status.toUpperCase()}
                  </span>
                </div>

                {/* Verisure Official Logo & Inscription: "I am Verisure" */}
                <div className="flex flex-col items-center justify-center my-1">
                  <img src="/official.png" alt="Verisure Logo" className="h-8 w-auto object-contain" />
                  <div className="text-center mt-1 mb-2">
                    <div className="text-sm font-black tracking-tight text-slate-900 leading-none">
                      I am <span className="text-[#ff5a00] bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 bg-clip-text text-transparent">Verisure</span>
                    </div>
                    <div className="w-10 h-0.5 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 mx-auto mt-1 rounded-full"></div>
                  </div>
                </div>

                <div className="p-2 bg-white border border-slate-200 rounded-xl my-1">
                  <VerisureQrCode
                    value={`${window.location.origin}/tag/${selectedPreviewQr.tagId}`}
                    size={150}
                    level="H"
                    showCenterBadge={true}
                    badgeTheme="light"
                  />
                </div>

                <div className="font-mono font-black text-base text-slate-950 tracking-wider mt-1">
                  {selectedPreviewQr.tagId}
                </div>
                <div className="text-[10px] text-slate-500 truncate max-w-[240px] mt-0.5">
                  {selectedPreviewQr.outletName}
                </div>

                <div className="w-full bg-orange-50 border border-orange-200 rounded-lg p-2.5 text-[9px] text-orange-950 mt-3 text-left">
                  <div className="font-bold text-orange-700 mb-0.5">Customer Activation:</div>
                  <div>1. Attach to your device.</div>
                  <div>2. Scan QR to register make, model & serial.</div>
                  <div>3. Certified repairs link automatically.</div>
                </div>
              </div>
            ) : (
              /* Official Card View with PNG/Print buttons */
              <div className="flex justify-center overflow-x-auto p-2 bg-slate-950 rounded-2xl border border-slate-800">
                <VerisureQrCard
                  value={`${window.location.origin}/tag/${selectedPreviewQr.tagId}`}
                  assetId={selectedPreviewQr.tagId}
                  title="Blank Outlet Hardware Tag"
                  subtitle={selectedPreviewQr.outletName}
                  size="compact"
                  showSizeSelector={false}
                  showActions={true}
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 w-full">
              {previewMode === 'sticker' && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await downloadVerisureStickerPng({
                        tagId: selectedPreviewQr.tagId,
                        url: `${window.location.origin}/tag/${selectedPreviewQr.tagId}`,
                        outletName: selectedPreviewQr.outletName,
                      });
                      toast.success('Downloaded sticker PNG with "I am Verisure" branding!');
                    } catch (err: any) {
                      toast.error('Download failed: ' + err.message);
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-bold py-3 rounded-xl text-xs transition flex items-center justify-center gap-1.5 shadow-md shadow-orange-500/20"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Sticker PNG
                </button>
              )}
              <button
                onClick={() => {
                  setSelectedPreviewQr(null);
                  setActiveTab('print');
                  setPrintFilterBatch(selectedPreviewQr.batchId || 'all');
                }}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 rounded-xl text-xs transition flex items-center justify-center gap-1.5"
              >
                Go to Print Sheet
              </button>
              <button
                onClick={() => {
                  window.open(`/tag/${selectedPreviewQr.tagId}`, '_blank');
                }}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-xl text-xs transition flex items-center justify-center gap-1.5"
              >
                Open Tag Activation &rarr;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Universal Bulk QR Print Station Modal */}
      <BulkQrPrintStationModal
        isOpen={isBulkStationOpen}
        onClose={() => setIsBulkStationOpen(false)}
        items={blankQrs.map((q) => ({
          id: q.id || q.tagId,
          tagId: q.tagId,
          qrUrl: `${window.location.origin}/tag/${q.tagId}`,
          outletName: q.outletName,
          category: 'OUTLET STICKER',
          title: q.deviceSummary?.make ? `${q.deviceSummary.make} ${q.deviceSummary.model}` : 'Unassigned Appliance Tag',
          status: q.status,
          isUsed: q.status === 'registered',
        }))}
        defaultTitle="OUTLET APPLIANCE BULK QR PRINT STATION"
        onDeleteItems={handleBulkDeleteQrs}
      />

      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        usage={subscriptionUsage}
        onUpgradeSuccess={() => loadUsage()}
      />
    </div>
  );
};
